import {t} from "../i18n.js"

import {mentionRow, mentionHighlight, escapeMentionText} from "./mention_presentation.mjs"
/**
 * SkillMention hook — unified @ autocomplete for the Skill/Schedule editor.
 *
 *   @Agent Name        — workspace agent / subagent (amber badge)
 *   @tool_name         — workspace tool (emerald badge)
 *   @User Name         — workspace member (user avatar color badge)
 *   @Credential        — workspace credential (warning badge)
 *   @collection_name   — data collection from Data Lake (cyan badge)
 *
 * All mention types use @ as prefix, differentiated by icon/color in the dropdown.
 *
 * Dropdown lives on document.body to survive LiveView DOM patches.
 * Uses event delegation on the dropdown (single listener, survives innerHTML).
 */

const MIRRORED_STYLE = [
  "boxSizing", "fontFamily", "fontSize", "fontWeight", "fontStyle", "fontVariant", "fontStretch",
  "fontKerning", "fontFeatureSettings", "fontVariationSettings", "lineHeight", "letterSpacing",
  "wordSpacing", "textTransform", "textIndent", "tabSize", "overflowWrap", "wordBreak",
  "paddingTop", "paddingRight", "paddingBottom", "paddingLeft",
  "borderTopWidth", "borderRightWidth", "borderBottomWidth", "borderLeftWidth",
  "borderTopLeftRadius", "borderTopRightRadius", "borderBottomRightRadius", "borderBottomLeftRadius",
]

export default {
  mounted() {
    this.aios = document.documentElement.classList.contains('aios-document')
    this.textarea = this.el
    this.candidates = []
    this.allAgents = []
    this.allTools = []
    this.allUsers = []
    this.allCredentials = []
    this.allCollections = []
    this.allSkills = []
    this.allContextBlocks = []
    this.mentionStart = -1
    this.selectedIdx = 0
    this.isOpen = false
    this._skipNextBlur = false

    this._loadData()
    this._createDropdown()

    // Overlay for styled mentions in the textarea.
    // Progressive enhancement: only flip the textarea text to transparent
    // when we actually found the overlay. If it's missing in prod (stale
    // asset, hook failed before this line, etc.), the textarea keeps its
    // native color so the user still sees what they type.
    this.overlay = document.getElementById(this.textarea.dataset.mentionOverlay || "skill-mention-overlay")
    this._hideNativeText()

    this._inputHandler = () => { this._detectMention(); this._syncOverlay(); this._pushMentions() }
    this._keydownHandler = (e) => this._onKeydown(e)
    this._blurHandler = () => {
      if (this._skipNextBlur) {
        this._skipNextBlur = false
        return
      }
      setTimeout(() => {
        if (document.activeElement !== this.textarea) {
          this._close()
        }
      }, 150)
    }

    this._scrollHandler = () => {
      if (!this.overlay) return
      this.overlay.scrollTop = this.textarea.scrollTop
      this.overlay.scrollLeft = this.textarea.scrollLeft
    }

    this.textarea.addEventListener("input", this._inputHandler)
    this.textarea.addEventListener("keydown", this._keydownHandler)
    this.textarea.addEventListener("blur", this._blurHandler)
    this.textarea.addEventListener("scroll", this._scrollHandler)

    // Server-driven fill: the Skill Builder (Monica drawer) generates the
    // skill instructions and the editor LV pushes them here so the textarea
    // + mention overlay update without the user re-typing. Re-syncs the
    // highlight overlay and re-pushes mentions so checked tools/agents stay
    // in sync with the new text.
    this.handleEvent("set_skill_instructions", ({ value }) => {
      if (this.textarea.dataset.mentionField && this.textarea.dataset.mentionField !== "instructions") return
      if (typeof value !== "string") return
      this.textarea.value = value
      this._close()
      this._syncOverlay()
      this._pushMentions()
    })

    this._alignOverlay()
    this._syncOverlay()
  },

  // The field's own glyphs go transparent so only the overlay paints the
  // text. It is an inline style, and LiveView's patch drops attributes the
  // server did not render — the async mention candidates (data-mention-*) and
  // every phx-change re-render the field — so it is re-applied on each update,
  // otherwise the native text reappears under the overlay's highlighted copy.
  _hideNativeText() {
    if (!this.overlay || !this.textarea?.style) return
    this.textarea.style.color = "transparent"
  },

  // The overlay is a mirror: every property that moves a glyph must match the
  // field, or the highlighted copy drifts away from the caret/selection.
  _alignOverlay() {
    if (!this.overlay || !this.textarea?.dataset?.mentionOverlay) return
    const ta = this.textarea, ov = this.overlay, style = getComputedStyle(ta)
    for (const key of MIRRORED_STYLE) ov.style[key] = style[key]
    const input = ta.tagName === "INPUT"
    ov.style.whiteSpace = input ? "pre" : "pre-wrap"
    if (!input) {
      // A classic scrollbar narrows the field's text box but not the
      // overlay's (overflow: hidden), so the overlay would wrap later.
      const bar = ta.offsetWidth - ta.clientWidth - parseFloat(style.borderLeftWidth || 0) - parseFloat(style.borderRightWidth || 0)
      ov.style.paddingRight = `${(parseFloat(style.paddingRight) || 0) + Math.max(0, bar)}px`
    }
  },

  updated() {
    // Throwing inside updated() aborts the LiveView DOM patch — keep this
    // resilient so a single bad mention candidate cannot freeze the editor.
    try {
      this._hideNativeText()
      this._loadData()
      this._syncOverlay()
      if (this.isOpen) this._detectMention()
    } catch (err) {
      console.error("[SkillMention] updated() error:", err)
    }
  },

  destroyed() {
    if (this.dropdown?.parentNode) this.dropdown.parentNode.removeChild(this.dropdown)
    if (this._mirror?.parentNode) this._mirror.parentNode.removeChild(this._mirror)
    if (this.textarea) {
      this.textarea.style.color = ""
      this.textarea.removeEventListener("input", this._inputHandler)
      this.textarea.removeEventListener("keydown", this._keydownHandler)
      this.textarea.removeEventListener("blur", this._blurHandler)
      this.textarea.removeEventListener("scroll", this._scrollHandler)
    }
  },

  _loadData() {
    try { this.allAgents = JSON.parse(this.textarea.dataset.mentionAgents || "[]") } catch (_) {}
    try { this.allTools = JSON.parse(this.textarea.dataset.mentionTools || "[]") } catch (_) {}
    try { this.allUsers = JSON.parse(this.textarea.dataset.mentionUsers || "[]") } catch (_) {}
    try { this.allCredentials = JSON.parse(this.textarea.dataset.mentionCredentials || "[]") } catch (_) {}
    try { this.allSkills = JSON.parse(this.textarea.dataset.mentionSkills || "[]") } catch (_) {}
    try { this.allContextBlocks = JSON.parse(this.textarea.dataset.mentionContextBlocks || "[]") } catch (_) {}
    try { this.allCollections = JSON.parse(this.textarea.dataset.mentionCollections || "[]") } catch (_) {}
  },

  // Build combined known mentions list with colors for overlay highlighting
  _buildKnownMentions() {
    const mentions = []
    // A null/empty name used to crash sort() inside updated(), aborting the
    // LiveView DOM patch. Filter aggressively here.
    const push = (name, color, kind) => {
      if (typeof name === "string" && name.length > 0) mentions.push({ name, color, kind })
    }
    this.allAgents.forEach(a => push(a.name, "var(--warn)", "agent"))
    this.allTools.forEach(t => push(t.name, "var(--s-300)", "tool"))
    this.allUsers.forEach(u => push(u.name || u.email, u.avatar_color || "var(--s-300)", "user"))
    this.allCredentials.forEach(c => push(c.name, "var(--badge-credential)", "key"))
    ;(this.allSkills || []).forEach(c => push(c.name, "var(--info)", "skill"))
    ;(this.allContextBlocks || []).forEach(c => push(c.name, "var(--info)", "context_block"))
    this.allCollections.forEach(c => push(c.label || c.collection, "var(--info)", "collection"))
    return mentions.sort((a, b) => b.name.length - a.name.length)
  },

  // ── Overlay: render styled mentions behind the textarea ───────────

  _syncOverlay() {
    if (!this.overlay) return
    this._alignOverlay()
    const val = this.textarea.value
    if (!val) {
      this.overlay.innerHTML = ""
      return
    }

    // A trailing newline is an empty last line in the field but collapses in
    // the overlay, which then cannot scroll as far as the field does.
    const esc = val.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;") + (val.endsWith("\n") ? " " : "")
    let highlighted = esc

    // Highlight all known @mentions by name, longest first to avoid partial matches
    const known = this._buildKnownMentions().map(m => ({...m, escapedName: escapeMentionText(m.name)}))
    if (known.length > 0) {
      const escaped = known.map(m => m.escapedName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      const re = new RegExp("@(" + escaped.join("|") + ")(?=\\s|$)", "g")
      highlighted = highlighted.replace(re, (_m, name) => {
        const m = known.find(k => k.escapedName === name)
        if (this.aios) return mentionHighlight(m?.kind, m?.name || name, "mark")
        const color = m ? m.color : "var(--s-300)"
        return `<span style="color:${color};font-weight:600">@${name}</span>`
      })
    }

    if (this.textarea.dataset?.mentionField && this.textarea.dataset.mentionField !== "instructions") {
      this.overlay.innerHTML = highlighted
      this.overlay.scrollTop = this.textarea.scrollTop
      this.overlay.scrollLeft = this.textarea.scrollLeft
      return
    }

    // ── Markdown styling (layout-neutral: no font-size, padding, or margin) ──
    // Code blocks (``` ... ```) — color only, keep delimiters visible
    highlighted = highlighted.replace(/```(\w*)\n([\s\S]*?)```/g, (_m, lang, code) => {
      return `<span style="color:var(--text-tertiary);">\`\`\`${lang}\n</span><span style="color:var(--accent-primary);">${code}</span><span style="color:var(--text-tertiary);">\`\`\`</span>`
    })
    // Inline code (`...`) — subtle background, no padding
    highlighted = highlighted.replace(/`([^`\n]+)`/g, (_m, code) => {
      return `<span style="color:var(--text-tertiary);">\`</span><span style="color:var(--accent-primary);">${code}</span><span style="color:var(--text-tertiary);">\`</span>`
    })
    // Bold (**...**) — weight only
    highlighted = highlighted.replace(/\*\*([^*\n]+)\*\*/g, (_m, text) => {
      return `<span style="color:var(--text-tertiary);">**</span><span style="font-weight:700;">${text}</span><span style="color:var(--text-tertiary);">**</span>`
    })
    // Italic (*...*) — style only
    highlighted = highlighted.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, (_m, text) => {
      return `<span style="color:var(--text-tertiary);">*</span><span style="font-style:italic;">${text}</span><span style="color:var(--text-tertiary);">*</span>`
    })
    // Headers (## at start of line) — bold + color, no size change
    highlighted = highlighted.replace(/^(#{1,3})\s+(.+)$/gm, (_m, hashes, text) => {
      return `<span style="color:var(--accent-primary);font-weight:700;">${hashes} ${text}</span>`
    })
    // Bullet lists (- or * at start of line) — color only
    highlighted = highlighted.replace(/^([*\-])\s+/gm, (_m, bullet) => {
      return `<span style="color:var(--accent-primary);font-weight:600;">${bullet}</span> `
    })

    this.overlay.innerHTML = highlighted
    this.overlay.scrollTop = this.textarea.scrollTop
    this.overlay.scrollLeft = this.textarea.scrollLeft
  },

  // ── Push extracted mentions to server ────────────────────────────

  _pushMentions() {
    if (this.textarea.dataset.mentionEvent === "false") return
    if (this._pushTimer) clearTimeout(this._pushTimer)
    this._pushTimer = setTimeout(() => {
      this.pushEvent("mentions_changed", { instructions: this.textarea.value })
    }, 400)
  },

  // ── Dropdown (body-mounted, event delegation) ─────────────────────

  _createDropdown() {
    const dd = document.createElement("div")
    dd.className = this.aios ? "skill-mention-dropdown menu aios-mention-dropdown" : "skill-mention-dropdown"
    Object.assign(dd.style, this.aios ? {display:"none", position:"fixed"} : {
      display: "none", position: "fixed", zIndex: "9999",
      maxHeight: "200px", overflowY: "auto", borderRadius: "8px",
      border: "1px solid var(--border-default)", background: "var(--bg-card)",
      boxShadow: "0 4px 16px rgba(0,0,0,0.12)", padding: "4px 0",
    })

    dd.addEventListener("mousedown", (e) => {
      e.preventDefault()
      const item = e.target.closest(".sm-item")
      if (!item) return
      const idx = parseInt(item.dataset.idx)
      if (idx >= 0 && idx < this.candidates.length) {
        this._skipNextBlur = true
        this._select(this.candidates[idx])
      }
    })

    dd.addEventListener("mouseover", (e) => {
      const item = e.target.closest(".sm-item")
      if (!item) return
      const idx = parseInt(item.dataset.idx)
      if (idx !== this.selectedIdx) {
        this.selectedIdx = idx
        this._highlightSelected()
      }
    })

    document.body.appendChild(dd)
    this.dropdown = dd
  },

  _positionDropdown() {
    const coords = this._getCaretCoords()
    if (!coords) {
      // Fallback: position at textarea top-left
      const rect = this.textarea.getBoundingClientRect()
      Object.assign(this.dropdown.style, {
        left: rect.left + "px",
        width: Math.min(rect.width, 360) + "px",
        top: "auto",
        bottom: (window.innerHeight - rect.top + 4) + "px",
      })
      return
    }

    const ddWidth = Math.min(360, window.innerWidth - 16)
    const ddHeight = this.dropdown.offsetHeight || 200

    // Position above the caret by default
    let top = coords.top - ddHeight - 4
    // If not enough space above, position below
    if (top < 8) {
      top = coords.bottom + 4
    }
    // Clamp horizontal to viewport
    let left = coords.left
    if (left + ddWidth > window.innerWidth - 8) {
      left = window.innerWidth - ddWidth - 8
    }
    if (left < 8) left = 8

    Object.assign(this.dropdown.style, {
      left: left + "px",
      top: top + "px",
      bottom: "auto",
      width: ddWidth + "px",
    })
  },

  /**
   * Returns { top, bottom, left } in viewport coords for the current caret position.
   * Uses a hidden mirror div that replicates the textarea's typography.
   */
  _getCaretCoords() {
    const ta = this.textarea
    const caret = ta.selectionStart

    // Create or reuse mirror
    if (!this._mirror) {
      this._mirror = document.createElement("div")
      this._mirror.style.cssText =
        "position:fixed;top:-9999px;left:-9999px;visibility:hidden;white-space:pre-wrap;word-wrap:break-word;overflow:hidden;"
      document.body.appendChild(this._mirror)
    }
    const mirror = this._mirror

    // Copy computed styles that affect text layout
    const cs = getComputedStyle(ta)
    const props = [
      "fontFamily", "fontSize", "fontWeight", "fontStyle", "letterSpacing",
      "lineHeight", "textTransform", "wordSpacing", "textIndent",
      "paddingTop", "paddingRight", "paddingBottom", "paddingLeft",
      "borderTopWidth", "borderRightWidth", "borderBottomWidth", "borderLeftWidth",
      "boxSizing",
    ]
    for (const p of props) mirror.style[p] = cs[p]
    mirror.style.width = ta.offsetWidth + "px"
    mirror.style.whiteSpace = ta.tagName === "INPUT" ? "pre" : "pre-wrap"
    // Same scrollbar allowance as the overlay, so the caret wraps where the field does.
    const bar = ta.offsetWidth - ta.clientWidth - parseFloat(cs.borderLeftWidth || 0) - parseFloat(cs.borderRightWidth || 0)
    if (bar > 0) mirror.style.paddingRight = `${(parseFloat(cs.paddingRight) || 0) + bar}px`

    // Fill mirror with text up to caret + a marker span
    const text = ta.value.substring(0, caret)
    const textNode = document.createTextNode(text)
    const marker = document.createElement("span")
    marker.textContent = "\u200b" // zero-width space
    mirror.innerHTML = ""
    mirror.appendChild(textNode)
    mirror.appendChild(marker)

    // Account for scroll
    mirror.scrollTop = ta.scrollTop

    // Get marker position relative to mirror, then convert to viewport coords
    const taRect = ta.getBoundingClientRect()
    const markerRect = marker.getBoundingClientRect()
    const mirrorRect = mirror.getBoundingClientRect()

    const relTop = markerRect.top - mirrorRect.top - ta.scrollTop
    const relLeft = markerRect.left - mirrorRect.left - (ta.scrollLeft || 0)

    return {
      top: taRect.top + relTop,
      bottom: taRect.top + relTop + parseFloat(cs.lineHeight) || 20,
      left: taRect.left + relLeft,
    }
  },

  // ── Mention detection (unified @ trigger) ─────────────────────────

  _detectMention() {
    const val = this.textarea.value
    const caret = this.textarea.selectionStart
    let scanPos = caret - 1
    let foundAt = -1

    while (scanPos >= 0) {
      const ch = val[scanPos]
      if (ch === "\n") break

      if (ch === "@") {
        const before = scanPos >= 1 ? val[scanPos - 1] : " "
        if (before === " " || before === "\n" || scanPos === 0) {
          foundAt = scanPos
        }
        break
      }
      scanPos--
    }

    if (foundAt === -1) {
      if (this.isOpen) this._close()
      return
    }

    const query = val.slice(foundAt + 1, caret)

    this.mentionStart = foundAt
    this._filter(query)

    if (this.candidates.length > 0) {
      if (!this.isOpen) this._open()
      else { this._renderItems(); this._positionDropdown() }
    } else {
      if (this.isOpen) this._close()
    }
  },

  _onKeydown(e) {
    if (!this.isOpen || this.candidates.length === 0) return

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        this.selectedIdx = Math.min(this.selectedIdx + 1, this.candidates.length - 1)
        this._highlightSelected()
        break
      case "ArrowUp":
        e.preventDefault()
        this.selectedIdx = Math.max(this.selectedIdx - 1, 0)
        this._highlightSelected()
        break
      case "Enter":
      case "Tab":
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        this._select(this.candidates[this.selectedIdx])
        break
      case "Escape":
        e.preventDefault()
        this._close()
        break
    }
  },

  // ── Filter: merge all types into unified list ─────────────────────

  _filter(query) {
    const q = query.toLowerCase()
    const named = items => (Array.isArray(items) ? items : []).filter(item => typeof item?.name === "string" && item.name.length > 0)

    const agentResults = named(this.allAgents)
      .filter(a => a.name.toLowerCase().includes(q))
      .map(a => ({ type: "agent", label: a.name, subtitle: t("Agent"), value: a.name, id: a.id, color: a.color || "var(--warn)" }))

    const toolResults = named(this.allTools)
      .filter(t => t.name.toLowerCase().includes(q))
      .map(tool => ({ type: "tool", label: tool.name, subtitle: t("Tool"), value: tool.name, id: tool.id }))

    const userResults = this.allUsers
      .filter(u => (u.name || u.email || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q))
      .map(u => ({ type: "user", label: u.name || u.email, subtitle: u.email || "", value: u.name || u.email, id: u.id, avatar_color: u.avatar_color || "var(--s-300)" }))

    const credResults = named(this.allCredentials)
      .filter(c => c.name.toLowerCase().includes(q))
      .map(c => ({ type: "credential", label: c.name, subtitle: c.type || t("Credential"), value: c.name, id: c.id, cred_type: c.type }))

    const colResults = this.allCollections
      .filter(c => (c.label || c.collection || "").toLowerCase().includes(q) || (c.app_name || "").toLowerCase().includes(q))
      .map(c => ({ type: "collection", label: c.label || c.collection, subtitle: c.app_name || t("Collection"), value: c.label || c.collection, id: c.app_id }))

    const extras = [[this.allSkills || [], "skill"], [this.allContextBlocks || [], "context_block"]].flatMap(([items, type]) => items.filter(c => (c.name || "").toLowerCase().includes(q)).map(c => ({type, label:c.name, value:c.name, subtitle:c.description || "", id:c.id})))
    this.candidates = [...agentResults, ...toolResults, ...userResults, ...credResults, ...colResults, ...extras].slice(0, 12)
    this.selectedIdx = 0
  },

  // ── Open / Close ──────────────────────────────────────────────────

  _open() {
    this.isOpen = true
    this.textarea.dataset.mentionOpen = "true"
    this.dropdown.style.display = "block"
    this._positionDropdown()
    this._renderItems()
  },

  _close() {
    this.isOpen = false
    delete this.textarea.dataset.mentionOpen
    this.dropdown.style.display = "none"
    this.mentionStart = -1
    this.candidates = []
  },

  // ── Render (innerHTML only, listeners are delegated) ──────────────

  _renderItems() {
    const ICON_ATTRS = `fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"`
    const SPARKLES_SVG = `<svg width="12" height="12" viewBox="0 0 24 24" ${ICON_ATTRS}><path d="M12 3l1.7 5.1L19 10l-5.3 1.9L12 17l-1.7-5.1L5 10l5.3-1.9L12 3z"/><path d="M19 16v4"/><path d="M21 18h-4"/></svg>`
    const WRENCH_SVG = `<svg width="12" height="12" viewBox="0 0 24 24" ${ICON_ATTRS}><path d="M14.7 6.3a5 5 0 0 0 5.6 5.6l-7.9 7.9a3 3 0 0 1-4.2-4.2l7.9-7.9z"/><path d="M4 20l5.5-5.5"/></svg>`
    const KEY_SVG = `<svg width="12" height="12" viewBox="0 0 24 24" ${ICON_ATTRS}><circle cx="15" cy="9" r="5"/><path d="M11.5 12.5L4 20"/><path d="M6.5 17.5H9"/><path d="M8.5 15.5H11"/></svg>`
    const TABLE_SVG = `<svg width="12" height="12" viewBox="0 0 24 24" ${ICON_ATTRS}><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 10h18"/><path d="M9 10v10"/><path d="M15 10v10"/></svg>`

    this.dropdown.innerHTML = this.candidates.map((c, i) => {
      const sel = i === this.selectedIdx ? " sm-sel" : ""

      if (this.aios) return mentionRow(c, i, i === this.selectedIdx, "sm")

      if (c.type === "agent") {
        return `<div class="sm-item${sel}" data-idx="${i}" style="padding:6px 10px;cursor:pointer;display:flex;align-items:center;gap:8px;border-radius:4px;margin:2px 4px;">
          <span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:6px;background:color-mix(in srgb, var(--warn) 10%, transparent);color:var(--warn);font-size:9px;flex-shrink:0;border:1px solid color-mix(in srgb, var(--warn) 20%, transparent)">${SPARKLES_SVG}</span>
          <div style="overflow:hidden">
            <div style="font-size:12px;color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${c.label}</div>
            <div style="font-size:10px;color:var(--text-tertiary)">${c.subtitle}</div>
          </div>
        </div>`
      } else if (c.type === "tool") {
        return `<div class="sm-item${sel}" data-idx="${i}" style="padding:6px 10px;cursor:pointer;display:flex;align-items:center;gap:8px;border-radius:4px;margin:2px 4px;">
          <span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:6px;background:color-mix(in srgb, var(--s-300) 10%, transparent);color:var(--s-300);font-size:9px;flex-shrink:0;border:1px solid color-mix(in srgb, var(--s-300) 20%, transparent)">${WRENCH_SVG}</span>
          <div style="overflow:hidden">
            <div style="font-size:12px;color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-family:monospace">${c.label}</div>
            <div style="font-size:10px;color:var(--text-tertiary)">${c.subtitle}</div>
          </div>
        </div>`
      } else if (c.type === "user") {
        const initial = c.label.charAt(0).toUpperCase()
        const color = c.avatar_color || "var(--s-300)"
        return `<div class="sm-item${sel}" data-idx="${i}" style="padding:6px 10px;cursor:pointer;display:flex;align-items:center;gap:8px;border-radius:4px;margin:2px 4px;">
          <span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;background:color-mix(in srgb, ${color} 10%, transparent);color:${color};border:1px solid color-mix(in srgb, ${color} 20%, transparent);font-size:10px;font-weight:600;flex-shrink:0">${initial}</span>
          <div style="overflow:hidden">
            <div style="font-size:12px;color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${c.label}</div>
            <div style="font-size:10px;color:var(--text-tertiary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${c.subtitle}</div>
          </div>
        </div>`
      } else if (c.type === "collection") {
        return `<div class="sm-item${sel}" data-idx="${i}" style="padding:6px 10px;cursor:pointer;display:flex;align-items:center;gap:8px;border-radius:4px;margin:2px 4px;">
          <span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:6px;background:color-mix(in srgb, var(--info) 10%, transparent);color:var(--info);font-size:9px;flex-shrink:0;border:1px solid color-mix(in srgb, var(--info) 20%, transparent)">${TABLE_SVG}</span>
          <div style="overflow:hidden">
            <div style="font-size:12px;color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-family:monospace">${c.label}</div>
            <div style="font-size:10px;color:var(--text-tertiary)">${c.subtitle}</div>
          </div>
        </div>`
      } else {
        // credential
        return `<div class="sm-item${sel}" data-idx="${i}" style="padding:6px 10px;cursor:pointer;display:flex;align-items:center;gap:8px;border-radius:4px;margin:2px 4px;">
          <span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:6px;background:color-mix(in srgb, var(--badge-credential) 10%, transparent);color:var(--badge-credential);font-size:9px;flex-shrink:0;border:1px solid color-mix(in srgb, var(--badge-credential) 20%, transparent)">${KEY_SVG}</span>
          <div style="overflow:hidden">
            <div style="font-size:12px;color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${c.label}</div>
            <div style="font-size:10px;color:var(--text-tertiary)">${c.subtitle}</div>
          </div>
        </div>`
      }
    }).join("")
  },

  _highlightSelected() {
    this.dropdown.querySelectorAll(".sm-item").forEach((el, i) => {
      el.classList.toggle("sm-sel", i === this.selectedIdx)
      if (this.aios) {
        el.setAttribute("aria-selected", String(i === this.selectedIdx))
        el.toggleAttribute("aria-current", i === this.selectedIdx)
      }
    })
  },

  // ── Select: always insert @name ───────────────────────────────────

  _select(candidate) {
    if (!candidate) return
    const val = this.textarea.value
    const before = val.slice(0, this.mentionStart)
    const afterCaret = val.slice(this.textarea.selectionStart)
    const insertion = `@${candidate.value} `
    const newVal = before + insertion + afterCaret
    const newCaret = before.length + insertion.length

    this._close()

    // Set value via native setter to ensure LiveView sees the change
    const nativeSetter = Object.getOwnPropertyDescriptor(
      this.textarea.tagName === "INPUT" ? HTMLInputElement.prototype : HTMLTextAreaElement.prototype, "value"
    ).set
    nativeSetter.call(this.textarea, newVal)

    // Focus first, then set caret — ensures the textarea is active before positioning
    this.textarea.focus()
    this.textarea.selectionStart = newCaret
    this.textarea.selectionEnd = newCaret

    // Sync overlay immediately so styled text is up to date
    this._syncOverlay()

    // Notify LiveView
    this.textarea.dispatchEvent(new Event("input", { bubbles: true }))
  },
}
